// Pane resizing for the speaker control.
//
// Three resizers, each backed by a single CSS variable on its container:
//   data-resize="cols"        →  --col-1  on  #stage
//   data-resize="left-rows"   →  --left-top  on  .left-column
//   data-resize="right-rows"  →  --right-top on  .right-column
//
// Each container's grid-template-columns/rows is `var(...) 6px 1fr`, so
// the second track auto-fills. We only ever update the first track size.
// Sizes persist in localStorage so layout survives reloads.

// v4 invalidates v3: TOC added below current+next; new resizer keys.
const STORAGE_KEY = 'rs_speaker_layout_v4';

const RESIZERS = {
  'cols': {
    containerSelector: '#stage',
    variable: '--col-1',
    axis: 'x',
  },
  'left-rows-top': {
    containerSelector: '.left-column',
    variable: '--left-top',
    axis: 'y',
  },
  'left-rows-bot': {
    containerSelector: '.left-column',
    variable: '--left-mid',
    axis: 'y',
    // For the second row resizer, the cursor position represents the boundary
    // between rows 2 and 3, so the value is (cursor% - row1%). Compute below.
    relativeTo: '--left-top',
  },
  'right-rows': {
    containerSelector: '.right-column',
    variable: '--right-top',
    axis: 'y',
  },
};

const MIN_PCT = 10;
const MAX_PCT = 90;

const loadSaved = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
};
const save = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch {}
};

/** Read padding for the relevant axis off a computed style. */
function paddingForAxis(cs, axis) {
  return axis === 'x'
    ? { start: parseFloat(cs.paddingLeft) || 0, end: parseFloat(cs.paddingRight)  || 0 }
    : { start: parseFloat(cs.paddingTop)  || 0, end: parseFloat(cs.paddingBottom) || 0 };
}

export function initResizers() {
  const stage = document.getElementById('stage');
  if (!stage) return;

  // Restore any saved sizes.
  const saved = loadSaved();
  for (const [key, cfg] of Object.entries(RESIZERS)) {
    const value = saved[key];
    if (value == null) continue;
    const target = stage.matches(cfg.containerSelector) ? stage : stage.querySelector(cfg.containerSelector);
    if (target) target.style.setProperty(cfg.variable, value);
  }

  // Wire each resizer.
  document.querySelectorAll('.resizer[data-resize]').forEach((handle) => {
    const cfg = RESIZERS[handle.dataset.resize];
    if (!cfg) return;
    const target = stage.matches(cfg.containerSelector) ? stage : handle.closest(cfg.containerSelector);
    if (!target) return;

    handle.addEventListener('mousedown', (downEv) => {
      downEv.preventDefault();
      handle.classList.add('dragging');
      // Block iframe pointer events while dragging so the cursor doesn't get
      // captured by the deck preview.
      document.body.style.userSelect = 'none';
      document.body.style.cursor = cfg.axis === 'x' ? 'col-resize' : 'row-resize';
      for (const f of stage.querySelectorAll('iframe')) f.style.pointerEvents = 'none';

      const onMove = (ev) => {
        // Recompute every frame — handles window resize during drag.
        const rect = target.getBoundingClientRect();
        const pad  = paddingForAxis(getComputedStyle(target), cfg.axis);
        const total = (cfg.axis === 'x' ? rect.width : rect.height) - pad.start - pad.end;
        const at    = (cfg.axis === 'x' ? ev.clientX - rect.left : ev.clientY - rect.top) - pad.start;
        let pct = (at / total) * 100;
        pct = Math.max(MIN_PCT, Math.min(MAX_PCT, pct));
        if (cfg.relativeTo) {
          // We're a second resizer that sets the size of the *middle* row.
          // The cursor position is the bottom edge of (row1 + row2), so the
          // middle-row size = cursor% − row1%.
          const r1 = parseFloat(getComputedStyle(target).getPropertyValue(cfg.relativeTo)) || 0;
          let mid = pct - r1;
          mid = Math.max(MIN_PCT, Math.min(MAX_PCT - r1, mid));
          target.style.setProperty(cfg.variable, mid.toFixed(2) + '%');
        } else {
          target.style.setProperty(cfg.variable, pct.toFixed(2) + '%');
        }
      };

      const onUp = () => {
        handle.classList.remove('dragging');
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        for (const f of stage.querySelectorAll('iframe')) f.style.pointerEvents = '';
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        const cur = loadSaved();
        cur[handle.dataset.resize] = target.style.getPropertyValue(cfg.variable);
        save(cur);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });
  });
}
